import React from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import {
  abbreviateNumber,
  formatStandardNumber,
} from "../../../utils/numberUtils";
import { InsufficientInfo } from "../ChartMessages/InsufficientInfo";
import { useNavigate, useParams } from "react-router-dom";
import { isOnWorkspaceConfigurationRoute } from "../../../utils/navigationUtils";
import { EditWorkspaceButton } from "../../../components/Workspace/EditWorkspaceButton";

interface MarketSelectableCardProps {
  label: string;
  totalSpend: number;
  numberOfCompanies: number;
  selected?: boolean;
  onSelect: () => void;
  isLoading: boolean;
  disabled?: boolean;
}

export const MarketSelectableCard: React.FC<MarketSelectableCardProps> = ({
  label,
  numberOfCompanies,
  onSelect,
  selected,
  totalSpend,
  isLoading,
  disabled = false,
}) => {
  const navigate = useNavigate();
  const { id: workspaceId } = useParams();

  if (isLoading) {
    return (
      <Skeleton
        className="market-selectable-card-skeleton"
        variant="rounded"
        sx={{
          minHeight: "130px",
          minWidth: "300px",
        }}
      />
    );
  }

  function onClickInsufficientInfo() {
    navigate(`/projects/${workspaceId}/configuration`);
  }

  if (disabled) {
    return (
      <InsufficientInfo
        onClick={onClickInsufficientInfo}
        isCard
        cardTitle={label}
      />
    );
  }

  return (
    <ButtonBase
      className={`market-selectable-button-card ${selected ? "active" : ""}`}
      id={`${label.toLowerCase()}-market-card`}
      onClick={onSelect}
      sx={{
        gap: "16px",
        minWidth: "300px",
        minHeight: "130px",
        padding: "20px 30px 25px 20px",
        display: "flex",
        borderRadius: "7px",
        flexDirection: "column",
        border: selected ? "solid 1px #1da5c3" : "solid 1px #eceff1",
        bgcolor: selected ? "rgba(29, 165, 195, 0.08)" : undefined,
        "&:hover": {
          cursor: "pointer",
          bgcolor: "rgba(29, 165, 195, 0.08)",
        },
      }}
    >
      <Box display="flex" justifyContent="space-between" width={"100%"}>
        <Typography variant="h2" sx={{ color: "#757575" }}>
          {label}
        </Typography>
        {!isOnWorkspaceConfigurationRoute(window.location.pathname) && (
          <EditWorkspaceButton
            onClick={onClickInsufficientInfo}
            asButton={false}
          />
        )}
      </Box>
      <Box
        display="flex"
        gap={3}
        justifyContent="space-between"
        alignSelf="stretch"
      >
        <Box
          gap="5px"
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          flex={1}
        >
          <Typography
            sx={{ fontSize: "32px", fontWeight: "bold", color: "#090909" }}
          >
            ${abbreviateNumber(totalSpend)}
          </Typography>
          <Typography variant="body2" sx={{ color: "#757575" }}>
            Total spend
          </Typography>
        </Box>
        <Box width="1px" height="41px" bgcolor="rgba(0, 0, 0, 0.12)" />
        <Box
          gap="2px"
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          flex={1}
        >
          <Typography sx={{ fontSize: "20px", color: "#757575" }}>
            {formatStandardNumber(numberOfCompanies)}
          </Typography>
          <Typography variant="body2" sx={{ color: "#757575" }}>
            No. of Companies
          </Typography>
        </Box>
      </Box>
    </ButtonBase>
  );
};
